import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState } from 'react';
import styled from 'styled-components';
import { BUNDLES } from './bundleData';
const BundleView = ({ selectedFeatures, onSelectBundle, approvedFeatures }) => {
    const [expandedBundles, setExpandedBundles] = useState(new Set());
    const toggleExpanded = (bundleId) => {
        setExpandedBundles(prev => {
            const next = new Set(prev);
            if (next.has(bundleId)) {
                next.delete(bundleId);
            }
            else {
                next.add(bundleId);
            }
            return next;
        });
    };
    const getApprovalStatus = (bundle) => {
        const approvedCount = bundle.featureIds.filter(id => approvedFeatures.has(id)).length;
        const totalCount = bundle.featureIds.length;
        if (approvedCount === totalCount) {
            return { type: 'full', label: 'Fully Deployed', color: '#10b981' };
        }
        else if (approvedCount > 0) {
            return { type: 'partial', label: `Partially Deployed (${approvedCount}/${totalCount})`, color: '#f59e0b' };
        }
        return null;
    };
    const getInvestmentColor = (investment) => {
        switch (investment) {
            case 'Low': return '#22c55e';
            case 'Medium': return '#3b82f6';
            case 'High': return '#f59e0b';
            case 'Very High': return '#ef4444';
            default: return '#6b7280';
        }
    };
    return (_jsx(Container, { children: _jsx(Grid, { children: BUNDLES.map(bundle => {
                const isExpanded = expandedBundles.has(bundle.id);
                const approvalStatus = getApprovalStatus(bundle);
                return (_jsxs(BundleCard, { children: [_jsxs(CardHeader, { gradient: bundle.gradient, children: [_jsxs(HeaderTop, { children: [_jsx(BundleName, { children: bundle.name }), approvalStatus && (_jsx(ApprovalBadge, { color: approvalStatus.color, children: approvalStatus.label }))] }), _jsx(Tagline, { children: bundle.tagline }), _jsxs(BadgeRow, { children: [_jsxs(InvestmentBadge, { color: getInvestmentColor(bundle.investment), children: [bundle.investment, " Investment"] }), _jsx(TimelineBadge, { children: bundle.timeline })] })] }), _jsxs(CardBody, { children: [_jsxs(PerfectForSection, { children: [_jsx(PerfectForLabel, { children: "Perfect For" }), _jsx(PerfectForText, { children: bundle.perfectFor })] }), _jsx(MockupContainer, { children: renderMockup(bundle.mockupType, bundle.color) }), _jsx(Description, { children: bundle.description }), _jsxs(FeaturePreview, { children: [_jsxs(FeatureCount, { children: [bundle.features.length, " Features Included"] }), _jsx(ExpandButton, { onClick: () => toggleExpanded(bundle.id), children: isExpanded ? 'Show Less' : 'Show All Features' })] }), isExpanded && (_jsx(FeatureList, { children: bundle.features.map((feature, idx) => (_jsxs(FeatureItem, { children: [_jsx(CheckIcon, { children: "\u2713" }), _jsxs(FeatureContent, { children: [_jsx(FeatureName, { children: feature.name }), _jsx(FeatureBenefit, { children: feature.benefit })] })] }, idx))) })), _jsx(SelectButton, { onClick: () => onSelectBundle(bundle.featureIds), gradient: bundle.gradient, children: "Select All Features in Bundle" })] })] }, bundle.id));
            }) }) }));
};
// Visual Mockup Renderer
const renderMockup = (type, color) => {
    switch (type) {
        case 'dashboard':
            return (_jsx(DashboardMockup, { children: _jsxs(DashboardGrid, { children: [_jsxs(DashboardCard, { style: { backgroundColor: `${color}15` }, children: [_jsx(CardBar, { style: { backgroundColor: color, width: '60%', height: '8px' } }), _jsx(CardBar, { style: { backgroundColor: `${color}80`, width: '80%', height: '6px' } }), _jsx(CardBar, { style: { backgroundColor: `${color}60`, width: '40%', height: '6px' } })] }), _jsxs(DashboardCard, { style: { backgroundColor: `${color}15` }, children: [_jsx(CardBar, { style: { backgroundColor: color, width: '70%', height: '8px' } }), _jsx(CardBar, { style: { backgroundColor: `${color}80`, width: '90%', height: '6px' } }), _jsx(CardBar, { style: { backgroundColor: `${color}60`, width: '50%', height: '6px' } })] }), _jsxs(DashboardCard, { style: { backgroundColor: `${color}15` }, children: [_jsx(CardBar, { style: { backgroundColor: color, width: '50%', height: '8px' } }), _jsx(CardBar, { style: { backgroundColor: `${color}80`, width: '75%', height: '6px' } }), _jsx(CardBar, { style: { backgroundColor: `${color}60`, width: '60%', height: '6px' } })] }), _jsxs(DashboardCard, { style: { backgroundColor: `${color}15` }, children: [_jsx(CardBar, { style: { backgroundColor: color, width: '65%', height: '8px' } }), _jsx(CardBar, { style: { backgroundColor: `${color}80`, width: '85%', height: '6px' } }), _jsx(CardBar, { style: { backgroundColor: `${color}60`, width: '45%', height: '6px' } })] })] }) }));
        case 'checklist':
            return (_jsx(ChecklistMockup, { children: [0.9, 0.75, 0.85, 0.95, 0.7].map((width, i) => (_jsxs(ChecklistItem, { children: [_jsx(Checkbox, { style: { backgroundColor: color } }), _jsx(ChecklistBar, { style: { backgroundColor: `${color}40`, width: `${width * 100}%` } })] }, i))) }));
        case 'chart':
            return (_jsxs(ChartMockup, { children: [_jsx(ChartBars, { children: [0.6, 0.8, 0.5, 0.9, 0.7, 0.85].map((height, i) => (_jsx(ChartBar, { style: {
                                height: `${height * 100}%`,
                                backgroundColor: i === 3 ? color : `${color}60`
                            } }, i))) }), _jsx(ChartAxis, { style: { borderColor: `${color}30` } })] }));
        case 'graph':
            return (_jsx(GraphMockup, { children: _jsxs("svg", { width: "100%", height: "100%", viewBox: "0 0 200 100", preserveAspectRatio: "none", children: [_jsx("defs", { children: _jsxs("linearGradient", { id: `gradient-${color}`, x1: "0%", y1: "0%", x2: "0%", y2: "100%", children: [_jsx("stop", { offset: "0%", stopColor: color, stopOpacity: "0.3" }), _jsx("stop", { offset: "100%", stopColor: color, stopOpacity: "0" })] }) }), _jsx("path", { d: "M 0 80 Q 50 60, 100 40 T 200 10", fill: "none", stroke: color, strokeWidth: "3" }), _jsx("path", { d: "M 0 80 Q 50 60, 100 40 T 200 10 L 200 100 L 0 100 Z", fill: `url(#gradient-${color})` })] }) }));
        case 'network':
            return (_jsxs(NetworkMockup, { children: [_jsx(NetworkNode, { style: { backgroundColor: color, top: '20%', left: '50%' }, large: true }), _jsx(NetworkNode, { style: { backgroundColor: `${color}90`, top: '50%', left: '20%' } }), _jsx(NetworkNode, { style: { backgroundColor: `${color}90`, top: '50%', left: '80%' } }), _jsx(NetworkNode, { style: { backgroundColor: `${color}70`, top: '75%', left: '35%' } }), _jsx(NetworkNode, { style: { backgroundColor: `${color}70`, top: '75%', left: '65%' } }), _jsxs("svg", { width: "100%", height: "100%", style: { position: 'absolute', top: 0, left: 0 }, children: [_jsx("line", { x1: "50%", y1: "20%", x2: "20%", y2: "50%", stroke: `${color}40`, strokeWidth: "2" }), _jsx("line", { x1: "50%", y1: "20%", x2: "80%", y2: "50%", stroke: `${color}40`, strokeWidth: "2" }), _jsx("line", { x1: "20%", y1: "50%", x2: "35%", y2: "75%", stroke: `${color}40`, strokeWidth: "2" }), _jsx("line", { x1: "80%", y1: "50%", x2: "65%", y2: "75%", stroke: `${color}40`, strokeWidth: "2" })] })] }));
        case 'mobile':
            return (_jsx(MobileMockup, { children: _jsx(MobileFrame, { style: { borderColor: color }, children: _jsxs(MobileScreen, { children: [_jsx(MobileHeader, { style: { backgroundColor: color } }), _jsxs(MobileContent, { children: [_jsx(MobileCard, { style: { backgroundColor: `${color}20` } }), _jsx(MobileCard, { style: { backgroundColor: `${color}20` } }), _jsx(MobileCard, { style: { backgroundColor: `${color}20` } })] })] }) }) }));
        case 'globe':
            return (_jsx(GlobeMockup, { children: _jsxs(GlobeCircle, { style: { borderColor: `${color}40` }, children: [_jsx(GlobeLine, { style: { borderColor: `${color}30` }, horizontal: true, top: "33%" }), _jsx(GlobeLine, { style: { borderColor: `${color}30` }, horizontal: true, top: "67%" }), _jsx(GlobeLine, { style: { borderColor: `${color}30` }, vertical: true, left: "33%" }), _jsx(GlobeLine, { style: { borderColor: `${color}30` }, vertical: true, left: "67%" }), _jsx(GlobePin, { style: { backgroundColor: color, top: '20%', left: '30%' } }), _jsx(GlobePin, { style: { backgroundColor: color, top: '40%', left: '60%' } }), _jsx(GlobePin, { style: { backgroundColor: color, top: '65%', left: '45%' } })] }) }));
        case 'lightning':
            return (_jsxs(LightningMockup, { children: [_jsx(LightningBolt, { style: { borderColor: color }, children: _jsx("svg", { width: "100%", height: "100%", viewBox: "0 0 100 100", children: _jsx("path", { d: "M 50 10 L 35 45 L 50 45 L 40 90 L 70 50 L 55 50 L 65 10 Z", fill: color }) }) }), _jsx(SpeedIndicator, { children: [0.4, 0.6, 0.8, 1].map((opacity, i) => (_jsx(SpeedBar, { style: {
                                backgroundColor: color,
                                opacity,
                                height: `${(i + 1) * 25}%`
                            } }, i))) })] }));
        case 'workflow':
            return (_jsx(WorkflowMockup, { children: [1, 2, 3, 4].map((step, i) => (_jsxs(React.Fragment, { children: [_jsx(WorkflowStep, { style: { backgroundColor: `${color}${i === 3 ? '' : '80'}` }, children: step }), i < 3 && _jsx(WorkflowArrow, { style: { borderTopColor: `${color}60` } })] }, step))) }));
        case 'grid':
            return (_jsx(GridMockup, { children: [...Array(9)].map((_, i) => (_jsxs(GridItem, { style: { backgroundColor: `${color}${i === 4 ? '' : '60'}` }, children: [_jsx(GridItemBar, { style: { backgroundColor: 'white', opacity: 0.3 } }), _jsx(GridItemBar, { style: { backgroundColor: 'white', opacity: 0.3 } })] }, i))) }));
        default:
            return null;
    }
};
// Styled Components
const Container = styled.div `
  padding: 2rem;
  background: var(--bg-primary);
  min-height: 100vh;
`;
const Grid = styled.div `
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(380px, 1fr));
  gap: 2rem;
  max-width: 1600px;
  margin: 0 auto;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`;
const BundleCard = styled.div `
  background: var(--bg-secondary);
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease, box-shadow 0.3s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 24px rgba(0, 0, 0, 0.15);
  }
`;
const CardHeader = styled.div `
  background: ${props => props.gradient};
  padding: 1.5rem;
  color: white;
`;
const HeaderTop = styled.div `
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 0.75rem;
  gap: 1rem;
`;
const BundleName = styled.h2 `
  font-size: 1.5rem;
  font-weight: 700;
  margin: 0;
  flex: 1;
`;
const ApprovalBadge = styled.span `
  background: ${props => props.color};
  color: white;
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  white-space: nowrap;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
`;
const Tagline = styled.p `
  font-size: 1rem;
  margin: 0 0 1rem 0;
  opacity: 0.95;
  font-weight: 500;
`;
const BadgeRow = styled.div `
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
`;
const InvestmentBadge = styled.span `
  background: ${props => props.color};
  color: white;
  padding: 0.375rem 0.875rem;
  border-radius: 20px;
  font-size: 0.875rem;
  font-weight: 600;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
`;
const TimelineBadge = styled.span `
  background: rgba(255, 255, 255, 0.25);
  color: white;
  padding: 0.375rem 0.875rem;
  border-radius: 20px;
  font-size: 0.875rem;
  font-weight: 600;
  backdrop-filter: blur(10px);
`;
const CardBody = styled.div `
  padding: 1.5rem;
`;
const PerfectForSection = styled.div `
  margin-bottom: 1.5rem;
  padding: 1rem;
  background: var(--bg-tertiary);
  border-radius: 8px;
  border-left: 4px solid var(--accent-primary);
`;
const PerfectForLabel = styled.div `
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  color: var(--accent-primary);
  margin-bottom: 0.5rem;
  letter-spacing: 0.5px;
`;
const PerfectForText = styled.div `
  font-size: 0.875rem;
  color: var(--text-primary);
  line-height: 1.5;
`;
const MockupContainer = styled.div `
  margin: 1.5rem 0;
  padding: 2rem;
  background: var(--bg-tertiary);
  border-radius: 12px;
  min-height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
`;
const Description = styled.p `
  font-size: 0.9375rem;
  color: var(--text-secondary);
  line-height: 1.6;
  margin-bottom: 1.5rem;
`;
const FeaturePreview = styled.div `
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid var(--border-color);
`;
const FeatureCount = styled.div `
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--text-primary);
`;
const ExpandButton = styled.button `
  background: none;
  border: none;
  color: var(--accent-primary);
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  padding: 0.25rem 0.5rem;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.8;
  }
`;
const FeatureList = styled.div `
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-bottom: 1.5rem;
  animation: slideDown 0.3s ease;

  @keyframes slideDown {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;
const FeatureItem = styled.div `
  display: flex;
  gap: 0.75rem;
  align-items: flex-start;
`;
const CheckIcon = styled.div `
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: var(--accent-primary);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  font-weight: 700;
  flex-shrink: 0;
  margin-top: 2px;
`;
const FeatureContent = styled.div `
  flex: 1;
`;
const FeatureName = styled.div `
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 0.25rem;
`;
const FeatureBenefit = styled.div `
  font-size: 0.8125rem;
  color: var(--text-secondary);
  line-height: 1.4;
`;
const SelectButton = styled.button `
  width: 100%;
  padding: 0.875rem 1.5rem;
  background: ${props => props.gradient};
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 12px rgba(0, 0, 0, 0.2);
  }

  &:active {
    transform: translateY(0);
  }
`;
// Mockup Styled Components
const DashboardMockup = styled.div `
  width: 100%;
  height: 100%;
`;
const DashboardGrid = styled.div `
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
  width: 100%;
`;
const DashboardCard = styled.div `
  padding: 1rem;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;
const CardBar = styled.div `
  border-radius: 4px;
`;
const ChecklistMockup = styled.div `
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;
const ChecklistItem = styled.div `
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;
const Checkbox = styled.div `
  width: 16px;
  height: 16px;
  border-radius: 4px;
  flex-shrink: 0;
`;
const ChecklistBar = styled.div `
  height: 8px;
  border-radius: 4px;
`;
const ChartMockup = styled.div `
  width: 100%;
  height: 150px;
  position: relative;
`;
const ChartBars = styled.div `
  display: flex;
  align-items: flex-end;
  justify-content: space-around;
  height: 100%;
  gap: 0.5rem;
  padding-bottom: 1rem;
`;
const ChartBar = styled.div `
  flex: 1;
  border-radius: 4px 4px 0 0;
  transition: height 0.3s ease;
`;
const ChartAxis = styled.div `
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  border-top: 2px solid;
`;
const GraphMockup = styled.div `
  width: 100%;
  height: 120px;
`;
const NetworkMockup = styled.div `
  width: 100%;
  height: 150px;
  position: relative;
`;
const NetworkNode = styled.div `
  position: absolute;
  width: ${props => props.large ? '24px' : '16px'};
  height: ${props => props.large ? '24px' : '16px'};
  border-radius: 50%;
  transform: translate(-50%, -50%);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
`;
const MobileMockup = styled.div `
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 200px;
`;
const MobileFrame = styled.div `
  width: 120px;
  height: 180px;
  border: 3px solid;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
`;
const MobileScreen = styled.div `
  width: 100%;
  height: 100%;
  background: var(--bg-primary);
`;
const MobileHeader = styled.div `
  height: 30px;
  width: 100%;
`;
const MobileContent = styled.div `
  padding: 0.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;
const MobileCard = styled.div `
  height: 32px;
  border-radius: 6px;
`;
const GlobeMockup = styled.div `
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 150px;
`;
const GlobeCircle = styled.div `
  width: 120px;
  height: 120px;
  border: 3px solid;
  border-radius: 50%;
  position: relative;
`;
const GlobeLine = styled.div `
  position: absolute;
  ${props => props.horizontal && `
    width: 100%;
    height: 0;
    border-top: 2px solid;
    top: ${props.top};
  `}
  ${props => props.vertical && `
    height: 100%;
    width: 0;
    border-left: 2px solid;
    left: ${props.left};
  `}
`;
const GlobePin = styled.div `
  position: absolute;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  box-shadow: 0 0 8px rgba(0, 0, 0, 0.3);
`;
const LightningMockup = styled.div `
  display: flex;
  justify-content: space-around;
  align-items: center;
  width: 100%;
  height: 150px;
  gap: 2rem;
`;
const LightningBolt = styled.div `
  width: 80px;
  height: 100px;
`;
const SpeedIndicator = styled.div `
  display: flex;
  align-items: flex-end;
  gap: 0.5rem;
  height: 80px;
`;
const SpeedBar = styled.div `
  width: 8px;
  border-radius: 4px;
`;
const WorkflowMockup = styled.div `
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  width: 100%;
`;
const WorkflowStep = styled.div `
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 700;
  font-size: 1.125rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
`;
const WorkflowArrow = styled.div `
  width: 0;
  height: 0;
  border-left: 12px solid transparent;
  border-right: 12px solid transparent;
  border-top: 12px solid;
  transform: rotate(-90deg);
`;
const GridMockup = styled.div `
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.5rem;
  width: 100%;
`;
const GridItem = styled.div `
  aspect-ratio: 1;
  border-radius: 6px;
  padding: 0.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;
const GridItemBar = styled.div `
  height: 4px;
  border-radius: 2px;
`;
export default BundleView;
